import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteSwapActionTypeResult,
  SwapActionType,
  SwapActionTypeApiItem,
  UpsertSwapActionTypeRequest,
} from './swap-action-type.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class SwapActionTypeService {
  private readonly endpoint = apiUrl('swapactiontype');

  constructor(private readonly http: HttpClient) {}

  getSwapActionTypes(): Observable<SwapActionType[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeSwapActionType(item))),
    );
  }

  createSwapActionType(payload: UpsertSwapActionTypeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateSwapActionType(id: number, payload: UpsertSwapActionTypeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteSwapActionType(id: number): Observable<DeleteSwapActionTypeResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteSwapActionTypeResult => ({ kind: 'success' })),
      catchError(() => of<DeleteSwapActionTypeResult>({
        kind: 'error',
        message: 'Unable to delete swap action type at this time. Please try again.',
      })),
    );
  }

  private normalizeSwapActionType(item: SwapActionTypeApiItem): SwapActionType {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: SwapActionTypeApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): SwapActionTypeApiItem[] {
    if (Array.isArray(response)) {
      return response as SwapActionTypeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as SwapActionTypeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as SwapActionTypeApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
